// ./client/src/admin/dashboard/TopBar.js
import React, { useRef, useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  alpha,
  Breadcrumbs,
  Link,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Divider,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockResetIcon from "@mui/icons-material/LockReset";
import { GlobalContext } from "../../context/GlobalContext";
import ThemeToggle from "../../common/ThemeToggle";
import ChangeLang from "../../common/ChangeLang"; // ← ADD THIS

const menuLabels = {
  dashboard: "Dashboard",
  "manage-users": "Manage Users",
  "manage-plans": "Manage Plans",
  orders: "Orders",
  "payment-gateway": "Payment Gateway",
  "site-settings": "Site Settings",
  "storage-settings": "Storage Settings",
  "web-translation": "Translation",
  "web-theme": "Theme Manager",
  "email-settings": "Email Settings",
};

const emptyForm = {
  email: "",
  current_password: "",
  new_password: "",
  confirm_password: "",
};

const TopBar = ({
  lang,
  theme,
  isMobile,
  isDark,
  selectedMenu,
  toggleColorMode,
  handleDrawerToggle,
}) => {
  const { hitAxios, web } = React.useContext(GlobalContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleOpen = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };
  const handleClose = () => {
    setDialogOpen(false);
    setForm(emptyForm);
  };

  const passwordMismatch =
    form.new_password &&
    form.confirm_password &&
    form.new_password !== form.confirm_password;

  const canSubmit =
    form.current_password &&
    (form.email || form.new_password) &&
    !passwordMismatch &&
    (form.new_password ? form.new_password === form.confirm_password : true);

  const handleSave = async () => {
    setSaving(true);
    const payload = { current_password: form.current_password };
    if (form.email.trim()) payload.email = form.email.trim();
    if (form.new_password) payload.new_password = form.new_password;
    const res = await hitAxios({
      path: "/api/admin/update-credentials",
      post: true,
      admin: true,
      obj: payload,
    });
    setSaving(false);
    if (res?.data?.success) handleClose();
  };

  return (
    <>
      {/* ── Mobile AppBar ── */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            bgcolor: "background.paper",
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: "text.primary",
          }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <IconButton edge="start" onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={800}
                color="#fff"
                sx={{ fontSize: "0.6rem" }}
              >
                TC
              </Typography>
            </Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ flexGrow: 1 }}
            >
              Admin Panel
            </Typography>

            {/* ← ADD ChangeLang here */}
            <ChangeLang />

            <IconButton size="small" onClick={handleOpen}>
              <ManageAccountsIcon fontSize="small" />
            </IconButton>
            <ThemeToggle
              isDark={isDark}
              toggleColorMode={toggleColorMode}
              theme={theme}
              size="small"
            />
          </Toolbar>
        </AppBar>
      )}

      {/* ── Desktop Top Bar ── */}
      {!isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            pt: 2.5,
            pb: 0,
          }}
        >
          <Stack spacing={0.5}>
            <Breadcrumbs
              separator={<NavigateNextIcon sx={{ fontSize: 14 }} />}
              sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5 } }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <HomeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Link
                  underline="hover"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem", cursor: "pointer" }}
                >
                  Admin
                </Link>
              </Stack>
              <Typography
                color="text.primary"
                sx={{ fontSize: "0.75rem", fontWeight: 600 }}
              >
                {menuLabels[selectedMenu] || selectedMenu}
              </Typography>
            </Breadcrumbs>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* ← ADD ChangeLang here */}
            <ChangeLang />

            <ThemeToggle
              isDark={isDark}
              toggleColorMode={toggleColorMode}
              theme={theme}
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Change Email / Password">
                <IconButton
                  size="small"
                  onClick={handleOpen}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                    },
                  }}
                >
                  <ManageAccountsIcon
                    sx={{ fontSize: 18, color: theme.palette.warning.main }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* ── Change Credentials Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LockResetIcon
                sx={{ fontSize: 20, color: theme.palette.warning.main }}
              />
            </Box>
            <Box>
              <Typography fontWeight={700} sx={{ lineHeight: 1.3 }}>
                Update Credentials
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Change your admin email or password
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            <TextField
              fullWidth
              size="small"
              label="Current Password *"
              type={showCurrent ? "text" : "password"}
              value={form.current_password}
              onChange={(e) =>
                setForm({ ...form, current_password: e.target.value })
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowCurrent((v) => !v)}
                    >
                      {showCurrent ? (
                        <VisibilityOff sx={{ fontSize: 16 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Divider>
              <Typography variant="caption" color="text.disabled">
                CHANGE EMAIL (optional)
              </Typography>
            </Divider>
            <TextField
              fullWidth
              size="small"
              label="New Email"
              type="email"
              placeholder="Leave blank to keep current"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <Divider>
              <Typography variant="caption" color="text.disabled">
                CHANGE PASSWORD (optional)
              </Typography>
            </Divider>
            <TextField
              fullWidth
              size="small"
              label="New Password"
              type={showNew ? "text" : "password"}
              placeholder="Leave blank to keep current"
              value={form.new_password}
              onChange={(e) =>
                setForm({ ...form, new_password: e.target.value })
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowNew((v) => !v)}
                    >
                      {showNew ? (
                        <VisibilityOff sx={{ fontSize: 16 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Confirm New Password"
              type={showConfirm ? "text" : "password"}
              placeholder="Repeat new password"
              value={form.confirm_password}
              onChange={(e) =>
                setForm({ ...form, confirm_password: e.target.value })
              }
              error={!!passwordMismatch}
              helperText={passwordMismatch ? "Passwords do not match" : ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowConfirm((v) => !v)}
                    >
                      {showConfirm ? (
                        <VisibilityOff sx={{ fontSize: 16 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleClose} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSubmit || saving}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              bgcolor: theme.palette.warning.main,
              "&:hover": { bgcolor: theme.palette.warning.dark },
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TopBar;
